import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import quoteService from '../../services/quoteService';
import artworkService from '../../services/artworkService';
import api from '../../services/api';

import './HomePage.css';

const HomePage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [stats, setStats] = useState({
        pending: 0,
        artworkCount: 0,
        profileCompletion: 0,
        recentActivity: []
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const [updatesRes, artworksRes, profileRes] = await Promise.all([
                    quoteService.getQuoteUpdates(),
                    user?.role?.toLowerCase() !== 'client' ? artworkService.getMyArtworks(1, 1) : Promise.resolve({ totalCount: 0 }),
                    user?.profileId ? api.get('/profile') : Promise.resolve({ data: {} })
                ]);

                const quotes = updatesRes?.quotes || [];
                const pending = quotes.filter(q => q.status?.toLowerCase() === 'pending').length;

                // Determine active messages for the user
                const isArtist = user?.role?.toLowerCase() !== 'client';
                const currentUserId = user?.id || user?.user_id;

                const activeMessages = quotes.filter(q => {
                    const statusStr = q.status?.toLowerCase();
                    if (isArtist && statusStr === 'pending') return true;
                    if (!isArtist && statusStr === 'artist_proposed') return true;
                    
                    if (['client_approved', 'booked', 'replied', 'artist_proposed'].includes(statusStr)) {
                        if (q.unreadCount > 0) return true;
                        // Needs attention if the last message is from the other party
                        if (q.lastMessage && q.lastMessage.senderId !== currentUserId) return true;
                    }
                    return false;
                }).sort((a, b) => new Date(b.lastMessageAt || b.updatedAt) - new Date(a.lastMessageAt || a.updatedAt));

                // Calculate profile completion
                const profile = profileRes?.data || {};
                const fields = ['artistName', 'bio', 'location', 'profilePicture', 'coverPhoto', 'whatsapp', 'instagram'];
                const completedFields = fields.filter(f => !!profile[f]).length;
                const profileCompletion = Math.round((completedFields / fields.length) * 100);

                setStats({
                    pending,
                    artworkCount: artworksRes?.totalCount || 0,
                    profileCompletion,
                    recentActivity: activeMessages.slice(0, 3) // Latest 3 active messages
                });
            } catch (error) {
                console.error("Error fetching dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [user?.role, user?.profileId, user?.id, user?.user_id]);

    const renderActivityItem = (item) => {
        const isArtist = user?.role !== 'client';
        const participantName = isArtist ? (item.clientName || t('unknown_client')) : (item.artistName || t('artist'));

        return (
            <div
                key={item.id}
                className="activity-item"
                onClick={() => navigate(isArtist ? '/artist/quotes' : '/my-quotes', { state: { selectedQuoteId: item.id } })}
            >
                <div className="activity-info">
                    <span className="activity-participant">{participantName}</span>
                    <span className="activity-status-badge" data-status={item.status?.toLowerCase()}>
                        {t(`stat_${item.status?.toLowerCase()}`)}
                    </span>
                </div>
                <p className="activity-description">
                    {typeof item.lastMessage === 'string'
                        ? item.lastMessage
                        : item.lastMessage?.content || t('no_messages_yet')}
                </p>
            </div>
        );
    };

    return (
        <div className="homepage-container">
            <div className="homepage-header">
                <h1 className="welcome-title">
                    {t('hello_user', { name: user?.username || t('artist') })}
                </h1>
                <p className="welcome-subtitle">{t('welcome_creative_space')}</p>
            </div>

            <div className="dashboard-sections">
                <section className="dashboard-stats-section">
                    <div className="stats-grid">
                        <div className="stat-card" onClick={() => navigate(user?.role?.toLowerCase() === 'client' ? '/my-quotes' : '/artist/quotes')}>
                            <span className="stat-value">{loading ? '...' : stats.pending}</span>
                            <span className="stat-label">{t('stat_pending')}</span>
                        </div>
                        <div className="stat-card" onClick={() => navigate('/profile/edit')}>
                            <div
                                className="profile-progress-circle"
                                style={{ '--percent': stats.profileCompletion }}
                            >
                                <span className="stat-icon">👤</span>
                            </div>
                            <span className="stat-label">
                                {stats.profileCompletion === 100 ? t('profile_complete') : t('complete_your_profile')}
                            </span>
                            <span className="completion-value">{stats.profileCompletion}%</span>
                        </div>
                        {user?.role?.toLowerCase() !== 'client' && (
                            <div className="stat-card" onClick={() => navigate('/artworks/manage')}>
                                {stats.artworkCount > 0 ? (
                                    <>
                                        <span className="stat-value">{loading ? '...' : stats.artworkCount}</span>
                                        <span className="stat-label">
                                            {stats.artworkCount === 1 ? t('artworks_published_single', { count: stats.artworkCount }) : t('artworks_published', { count: stats.artworkCount })}
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span className="stat-icon">🎨</span>
                                        <span className="stat-label">{loading ? '...' : t('publish_first_artwork')}</span>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </section>

                <section className="dashboard-activity-section">
                    <h2 className="section-title">{t('messages')}</h2>
                    <div className="activity-feed">
                        {loading ? (
                            <div className="loading-placeholder">{t('loading')}</div>
                        ) : stats.recentActivity.length > 0 ? (
                            stats.recentActivity.map(renderActivityItem)
                        ) : (
                            <div className="empty-activity">
                                <p>{t('no_messages_attention', 'Nenhuma mensagem recente precisando de atenção.')}</p>
                                <button className="explore-btn" onClick={() => navigate('/explore')}>
                                    {t('browse_artists')}
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default HomePage;
